import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import AddressAutocomplete from '../AddressAutocomplete';
import styles from './CreateShipmentForm.module.css';
import { customList } from 'country-codes-list';
import { api } from '../../apiConfig';
import { blockWheelOnNumber } from '../../utils/numberInput';

// Dynamically generate the country list for phone input
const PHONE_COUNTRIES = Object.entries(customList('countryCode', '{countryNameEn}|{countryCode}|{countryCallingCode}|{isObsolete}'))
  .map(([code, str]) => {
    const [name, countryCode, dial, obsolete] = str.split('|');
    return { code: countryCode, name, dial: dial ? `+${dial}` : '', obsolete, format: 'XXX-XXX-XXXX' };
  })
  .filter(c => c.dial && c.obsolete !== 'true')
  .sort((a, b) => a.name.localeCompare(b.name));

function getCountryByCode(code) {
  const country = PHONE_COUNTRIES.find(c => c.code === code);
  return country || { code: 'US', dial: '+1', format: 'XXX-XXX-XXXX' };
}

const initialState = {
  recipient_name: '',
  recipient_phone: '',
  recipient_email: '',
  recipient_address_line1: '',
  recipient_address_line2: '',
  recipient_city: '',
  recipient_state: '',
  recipient_postal_code: '',
  recipient_country: 'US',
  ship_from_name: '',
  ship_from_phone: '',
  ship_from_address_line1: '',
  ship_from_address_line2: '',
  ship_from_city: '',
  ship_from_state: '',
  ship_from_postal_code: '',
  ship_from_country: 'US',
  weight: '',
  weight_unit: 'lb',
  width: '',
  height: '',
  depth: '',
  dimension_unit: 'in',
  packaging_type: 'Box',
  reference: '',
  notes: '',
  recipient_phone_country: 'US',
  ship_from_phone_country: 'US',
  // Customs (international)
  customs_description: '',
  customs_quantity: 1,
  customs_value: '',
  customs_hs_code: '',
  customs_origin_country: 'US',
  customs_contents: 'merchandise', // merchandise|gift|documents|returned_goods|sample|other
  customs_non_delivery: 'return' // return|treat_as_abandoned (destroy)
};

const CreateShipmentForm = ({ onSubmit }) => {
  const formatPhoneNumber = (value, countryCode) => {
    const country = getCountryByCode(countryCode);
    const numbers = value.replace(/\D/g, '');
    let formatted = '';
    let i = 0;
    for (const char of country.format) {
      if (char === 'X') {
        if (numbers[i]) {
          formatted += numbers[i++];
        } else {
          break;
        }
      } else {
        formatted += char;
      }
    }
    return formatted;
  };

  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [addressChanged, setAddressChanged] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const { currentUser } = useAuth();

  // Fetch user profile on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      console.log('🚀 Starting profile fetch, currentUser:', !!currentUser);
      
      if (!currentUser) {
        console.log('❌ No current user, skipping profile fetch');
        setProfileLoading(false);
        return;
      }

      try {
        console.log('🔑 Getting ID token...');
        const token = await currentUser.getIdToken();
        console.log('✅ Got token, making API call...');
        
        const response = await api.get('/users/profile');

        console.log('📡 API response status:', response.status);

        if (response.status === 200) {
          const profile = response.data;
          console.log('🔍 FULL PROFILE DATA RECEIVED:', profile);
          setUserProfile(profile);
          
          // Auto-populate ship-from fields if user has shipping address
          if (profile.shipping_address_line1) {
            console.log('📋 Profile data found:', {
              shipping_name: profile.shipping_name,
              shipping_address_line1: profile.shipping_address_line1,
              shipping_city: profile.shipping_city,
              shipping_state: profile.shipping_state
            });
            
            setForm(prev => {
              const newFormData = {
                ...prev,
                ship_from_name: profile.shipping_name || '',
                ship_from_phone: profile.shipping_phone || '',
                ship_from_address_line1: profile.shipping_address_line1 || '',
                ship_from_address_line2: profile.shipping_address_line2 || '',
                ship_from_city: profile.shipping_city || '',
                ship_from_state: profile.shipping_state || '',
                ship_from_postal_code: profile.shipping_postal_code || '',
                ship_from_country: profile.shipping_country || '',
              };
              
              console.log('🔄 SETTING FORM DATA TO:', newFormData);
              return newFormData;
            });
            
            setProfileLoaded(true);
            console.log('✅ Auto-populated ship-from address from user profile');
            toast.success('Ship-from address loaded from your profile!', { autoClose: 3000 });
          } else {
            console.log('⚠️ No shipping address found in profile');
            console.log('🔍 Profile keys:', Object.keys(profile));
            console.log('🔍 shipping_address_line1 value:', profile.shipping_address_line1);
            toast.info('Add your shipping address in your profile for auto-fill', { autoClose: 5000 });
          }
        } else {
          console.error('❌ Profile fetch failed with status:', response.status);
          console.error('❌ Error response:', response.data);
        }
      } catch (error) {
        console.error('❌ Error fetching user profile:', error);
        console.error('❌ Error stack:', error.stack);
      } finally {
        console.log('🏁 Profile fetch completed, setting loading to false');
        setProfileLoading(false);
      }
    };

    fetchUserProfile();
  }, [currentUser]);

  // Debug: Log form state changes
  useEffect(() => {
    console.log('📝 FORM STATE CHANGED:', {
      ship_from_name: form.ship_from_name,
      ship_from_address_line1: form.ship_from_address_line1,
      ship_from_city: form.ship_from_city,
      profileLoaded
    });
  }, [form.ship_from_name, form.ship_from_address_line1, form.ship_from_city, profileLoaded]);

  // Update user profile when ship-from address changes
  const updateUserShippingAddress = async (shippingData) => {
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await api.put('/users/profile', {
        shipping_name: shippingData.ship_from_name,
        shipping_phone: shippingData.ship_from_phone,
        shipping_address_line1: shippingData.ship_from_address_line1,
        shipping_address_line2: shippingData.ship_from_address_line2,
        shipping_city: shippingData.ship_from_city,
        shipping_state: shippingData.ship_from_state,
        shipping_postal_code: shippingData.ship_from_postal_code,
        shipping_country: shippingData.ship_from_country,
      });

      if (response.status === 200) {
        console.log('✅ Updated user shipping address in profile');
        setAddressChanged(false);
        toast.success('Shipping address saved to your profile');
      }
    } catch (error) {
      console.error('Error updating user shipping address:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Track if ship-from address fields have changed
    if (name.startsWith('ship_from_')) {
      setAddressChanged(true);
    }
    
    if (name === 'recipient_phone') {
      setForm((prev) => ({
        ...prev,
        [name]: formatPhoneNumber(value, prev.recipient_phone_country)
      }));
    } else if (name === 'ship_from_phone') {
      setForm((prev) => ({
        ...prev,
        [name]: formatPhoneNumber(value, prev.ship_from_phone_country)
      }));
    } else if (name === 'recipient_phone_country' || name === 'ship_from_phone_country') {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        // Reset phone value to reformat for new country
        [name === 'recipient_phone_country' ? 'recipient_phone' : 'ship_from_phone']:
          formatPhoneNumber(prev[name === 'recipient_phone_country' ? 'recipient_phone' : 'ship_from_phone'], value)
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePlaceSelect = (addressData, place, prefix) => {
    // Track if ship-from address has changed
    if (prefix === 'ship_from_') {
      setAddressChanged(true);
    }
    
    // Debug: Log what Google Places is setting
    console.log('🗺️ Google Places autocomplete triggered:', {
      prefix,
      addressData,
      place: place?.formatted_address
    });

    // Only fill fields Places actually returned — never wipe existing values with ''
    setForm(prev => {
      const next = { ...prev };
      if (addressData.street_address || addressData.formatted_address) {
        next[`${prefix}address_line1`] = addressData.street_address || addressData.formatted_address;
      }
      if (addressData.city) next[`${prefix}city`] = addressData.city;
      if (addressData.state) next[`${prefix}state`] = addressData.state;
      if (addressData.postal_code) next[`${prefix}postal_code`] = addressData.postal_code;
      if (addressData.country) next[`${prefix}country`] = addressData.country;
      return next;
    });
  };

  // Save ship-from address to profile
  const handleSaveShipFromAddress = async () => {
    await updateUserShippingAddress(form);
  };

  const validate = () => {
    const errs = {};
    if (!form.recipient_name) errs.recipient_name = 'Required';
    if (!form.recipient_address_line1) errs.recipient_address_line1 = 'Required';
    if (!form.recipient_city) errs.recipient_city = 'Required';
    if (!form.recipient_state) errs.recipient_state = 'Required';
    if (!form.recipient_postal_code) errs.recipient_postal_code = 'Required';
    if (!form.recipient_country) errs.recipient_country = 'Required';
    if (!form.weight) errs.weight = 'Required';
    if (!form.width) errs.width = 'Required';
    if (!form.height) errs.height = 'Required';
    if (!form.depth) errs.depth = 'Required';
    if (!form.packaging_type) errs.packaging_type = 'Required';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error('Please fill in all required shipping fields');
      const firstErrorField = Object.keys(errs)[0];
      const el = document.getElementById(firstErrorField);
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus?.();
      }
      return;
    }
    setSubmitting(true);
    try {
      const getE164 = (phone, countryCode) => {
        const country = getCountryByCode(countryCode);
        if (!country) return phone; // Fallback if country not found
        const numbers = phone.replace(/\D/g, '');
        return country.dial + numbers;
      };
      
      // Update user profile with ship-from address if it changed
      if (addressChanged) {
        await updateUserShippingAddress(form);
      }
      
      // Debug: Log the form data being submitted
      console.log('📦 Form submission data:', {
        recipient_city: form.recipient_city,
        recipient_state: form.recipient_state,
        recipient_address_line1: form.recipient_address_line1,
        ship_from_city: form.ship_from_city,
        ship_from_state: form.ship_from_state,
        ship_from_address_line1: form.ship_from_address_line1,
        full_form: form
      });
      
      const result = await onSubmit({
        ...form,
        recipient_phone: getE164(form.recipient_phone, form.recipient_phone_country),
        ship_from_phone: getE164(form.ship_from_phone, form.ship_from_phone_country),
      });
      // Only clear the form after rates were actually returned
      if (result?.success) {
        setForm(initialState);
        setAddressChanged(false);
        setErrors({});
        setProfileLoaded(false);
      }
    } catch (error) {
      // Parent toasts API errors; keep form data so the user can retry
      console.error('Shipment form submit failed:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} autoComplete="off">
      <fieldset className={styles.fieldset} disabled={submitting} autoComplete="section-recipient">
        <legend className={styles.legend}>Recipient</legend>
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="recipient_name">Name</label>
            <input id="recipient_name" name="recipient_name" autoComplete="section-recipient name" value={form.recipient_name} onChange={handleChange} placeholder="Full Name" aria-invalid={!!errors.recipient_name} />
            {errors.recipient_name && <span className={styles.error}>{errors.recipient_name}</span>}
          </div>
          <div className={styles.field}>
            <label htmlFor="recipient_phone">Phone</label>
            <div className={styles.phoneRow}>
              <select name="recipient_phone_country" value={form.recipient_phone_country} onChange={handleChange} className={styles.phoneCountrySelect} aria-label="Recipient country code" autoComplete="section-recipient tel-country-code">
                {PHONE_COUNTRIES.map(c => (<option key={c.code} value={c.code}>{c.dial}</option>))}
              </select>
              <input id="recipient_phone" name="recipient_phone" type="tel" inputMode="tel" autoComplete="section-recipient tel-national" placeholder="000-000-0000" value={form.recipient_phone} onChange={handleChange} maxLength={20} />
            </div>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="recipient_email">Email</label>
            <input id="recipient_email" name="recipient_email" autoComplete="section-recipient email" value={form.recipient_email} onChange={handleChange} placeholder="Email" />
          </div>
          <div className={styles.field}>
            <label htmlFor="recipient_address_line1">Address Line 1</label>
            <AddressAutocomplete
              id="recipient_address_line1"
              value={form.recipient_address_line1}
              onChange={(value) => setForm(prev => ({ ...prev, recipient_address_line1: value }))}
              onPlaceChanged={(addressData, place) => handlePlaceSelect(addressData, place, 'recipient_')}
              placeholder="Start typing address..."
              autoComplete="section-recipient address-line1"
            />
            {errors.recipient_address_line1 && <span className={styles.error}>{errors.recipient_address_line1}</span>}
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="recipient_address_line2">Address Line 2</label>
            <input id="recipient_address_line2" name="recipient_address_line2" autoComplete="section-recipient address-line2" value={form.recipient_address_line2} onChange={handleChange} placeholder="Apt, Suite, etc." />
          </div>
          <div className={styles.field}>
            <label htmlFor="recipient_city">City</label>
            <input id="recipient_city" name="recipient_city" autoComplete="section-recipient address-level2" value={form.recipient_city} onChange={handleChange} placeholder="City" aria-invalid={!!errors.recipient_city} />
            {errors.recipient_city && <span className={styles.error}>{errors.recipient_city}</span>}
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="recipient_state">State</label>
            <input id="recipient_state" name="recipient_state" autoComplete="section-recipient address-level1" value={form.recipient_state} onChange={handleChange} placeholder="State" aria-invalid={!!errors.recipient_state} />
            {errors.recipient_state && <span className={styles.error}>{errors.recipient_state}</span>}
          </div>
          <div className={styles.field}>
            <label htmlFor="recipient_postal_code">Postal Code</label>
            <input id="recipient_postal_code" name="recipient_postal_code" autoComplete="section-recipient postal-code" value={form.recipient_postal_code} onChange={handleChange} placeholder="Postal Code" aria-invalid={!!errors.recipient_postal_code} />
            {errors.recipient_postal_code && <span className={styles.error}>{errors.recipient_postal_code}</span>}
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="recipient_country">Country</label>
            <input id="recipient_country" name="recipient_country" autoComplete="section-recipient country" value={form.recipient_country} onChange={handleChange} placeholder="Country" aria-invalid={!!errors.recipient_country} />
            {errors.recipient_country && <span className={styles.error}>{errors.recipient_country}</span>}
          </div>
          <div className={styles.field}></div>
        </div>
      </fieldset>
      <fieldset className={styles.fieldset} autoComplete="section-shipfrom">
        <legend className={styles.legend}>
          📦 Ship From
          {profileLoading && <span className={styles.loadingText}> (Loading profile...)</span>}
          {profileLoaded && !profileLoading && (
            <span className={styles.profileText}> ✅ Auto-filled from your profile</span>
          )}
          {!profileLoading && !profileLoaded && (
            <span className={styles.emptyText}> (Enter your shipping address)</span>
          )}
        </legend>
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="ship_from_name">Name</label>
            <input 
              id="ship_from_name" 
              name="ship_from_name"
              autoComplete="section-shipfrom name"
              value={form.ship_from_name} 
              onChange={handleChange} 
              placeholder="Full Name" 
              className={profileLoaded && form.ship_from_name ? styles.autoFilledInput : ''}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="ship_from_phone">Phone</label>
            <div className={styles.phoneRow}>
              <select 
                name="ship_from_phone_country" 
                value={form.ship_from_phone_country} 
                onChange={handleChange} 
                className={styles.phoneCountrySelect} 
                aria-label="Ship from country code"
                autoComplete="section-shipfrom tel-country-code"
              >
                {PHONE_COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{c.dial}</option>
                ))}
              </select>
              <input 
                id="ship_from_phone" 
                name="ship_from_phone" 
                type="tel" 
                inputMode="tel" 
                autoComplete="section-shipfrom tel-national" 
                placeholder="000-000-0000" 
                value={form.ship_from_phone} 
                onChange={handleChange} 
                maxLength={20} 
              />
            </div>
          </div>
        </div>
        
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="ship_from_address_line1">Address Line 1</label>
            <AddressAutocomplete
              id="ship_from_address_line1"
              value={form.ship_from_address_line1}
              onChange={(value) => setForm(prev => ({ ...prev, ship_from_address_line1: value }))}
              onPlaceChanged={(addressData, place) => handlePlaceSelect(addressData, place, 'ship_from_')}
              placeholder="Start typing address..."
              autoComplete="section-shipfrom address-line1"
              className={profileLoaded && form.ship_from_address_line1 ? styles.autoFilledInput : ''}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="ship_from_address_line2">Address Line 2</label>
            <input 
              id="ship_from_address_line2" 
              name="ship_from_address_line2"
              autoComplete="section-shipfrom address-line2"
              value={form.ship_from_address_line2} 
              onChange={handleChange} 
              placeholder="Apt, Suite, etc." 
            />
          </div>
        </div>
        
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="ship_from_city">City</label>
            <input 
              id="ship_from_city" 
              name="ship_from_city"
              autoComplete="section-shipfrom address-level2"
              value={form.ship_from_city} 
              onChange={handleChange} 
              placeholder="City" 
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="ship_from_state">State</label>
            <input 
              id="ship_from_state" 
              name="ship_from_state"
              autoComplete="section-shipfrom address-level1"
              value={form.ship_from_state} 
              onChange={handleChange} 
              placeholder="State" 
            />
          </div>
        </div>
        
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="ship_from_postal_code">Postal Code</label>
            <input 
              id="ship_from_postal_code" 
              name="ship_from_postal_code"
              autoComplete="section-shipfrom postal-code"
              value={form.ship_from_postal_code} 
              onChange={handleChange} 
              placeholder="Postal Code" 
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="ship_from_country">Country</label>
            <input 
              id="ship_from_country" 
              name="ship_from_country"
              autoComplete="section-shipfrom country"
              value={form.ship_from_country} 
              onChange={handleChange} 
              placeholder="Country" 
            />
          </div>
        </div>
        
        {/* Save Address Button */}
        {addressChanged && (
          <div className={styles.row}>
            <div className={styles.field} style={{ gridColumn: '1 / span 2' }}>
              <button 
                type="button" 
                onClick={handleSaveShipFromAddress}
                className={styles.saveAddressButton}
              >
                💾 Save this address to your profile
              </button>
            </div>
          </div>
        )}
      </fieldset>
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Package Details</legend>
        <div className={styles.row}>
          <div className={styles.field}><label htmlFor="weight">Weight</label><div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}><input ref={blockWheelOnNumber} id="weight" name="weight" type="number" min="0" step="any" value={form.weight} onChange={handleChange} placeholder="e.g. 2" /><select name="weight_unit" value={form.weight_unit} onChange={handleChange}><option value="lb">lb</option><option value="kg">kg</option></select></div>{errors.weight && <span className={styles.error}>{errors.weight}</span>}</div>
          <div className={styles.field}><label htmlFor="width">Width</label><div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}><input ref={blockWheelOnNumber} id="width" name="width" type="number" min="0" step="any" value={form.width} onChange={handleChange} placeholder="e.g. 12" /><select name="dimension_unit" value={form.dimension_unit} onChange={handleChange}><option value="in">in</option><option value="cm">cm</option></select></div>{errors.width && <span className={styles.error}>{errors.width}</span>}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.field}><label htmlFor="height">Height</label><input ref={blockWheelOnNumber} id="height" name="height" type="number" min="0" step="any" value={form.height} onChange={handleChange} placeholder="e.g. 16" />{errors.height && <span className={styles.error}>{errors.height}</span>}</div>
          <div className={styles.field}><label htmlFor="depth">Depth</label><input ref={blockWheelOnNumber} id="depth" name="depth" type="number" min="0" step="any" value={form.depth} onChange={handleChange} placeholder="Depth" />{errors.depth && <span className={styles.error}>{errors.depth}</span>}</div>
        </div>
        <div className={styles.row}>
          <div className={styles.field}><label htmlFor="packaging_type">Packaging Type</label><select id="packaging_type" name="packaging_type" value={form.packaging_type} onChange={handleChange}><option value="">Select type</option><option value="Box">Box</option><option value="Tube">Tube</option><option value="Envelope">Envelope</option></select>{errors.packaging_type && <span className={styles.error}>{errors.packaging_type}</span>}</div>
          <div className={styles.field}><label htmlFor="reference">Reference</label><input id="reference" name="reference" value={form.reference} onChange={handleChange} placeholder="Reference" /></div>
        </div>
        <div className={styles.row}>
          <div className={styles.field} style={{ gridColumn: '1 / span 2' }}><label htmlFor="notes">Notes</label><textarea id="notes" name="notes" value={form.notes} onChange={handleChange} rows={2} placeholder="Notes about the shipment..." /></div>
        </div>
        {(() => {
          const w = Number(form.width) || 0;
          const h = Number(form.height) || 0;
          const d = Number(form.depth) || 0;
          const maxSide = Math.max(w, h, d);
          return maxSide >= 96 ? (
            <div className={styles.row}>
              <div className={styles.field} style={{ gridColumn: '1 / span 2', color: '#b45309', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 6, padding: '10px 12px' }}>
                <strong>Heads up:</strong> Oversize shipments (≥ 96") may require SEKO LTL.
                Set weight to at least 150 lb and leave signature/confirmation blank for quotes. SEKO LTL shipments must be fully crated and on a pallet for pickup, and the carrier will call to confirm details and schedule pickup.
              </div>
            </div>
          ) : null;
        })()}
      </fieldset>
      {/* Customs (International) */}
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Customs (International Shipments)</legend>
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="customs_description">Contents Description</label>
            <input id="customs_description" name="customs_description" value={form.customs_description} onChange={handleChange} placeholder="e.g. Original artwork on canvas" />
          </div>
          <div className={styles.field}>
            <label htmlFor="customs_quantity">Quantity</label>
            <input id="customs_quantity" name="customs_quantity" type="number" min="1" value={form.customs_quantity} onChange={handleChange} />
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="customs_value">Declared Value (USD)</label>
            <input id="customs_value" name="customs_value" type="number" min="0" step="0.01" value={form.customs_value} onChange={handleChange} placeholder="e.g. 2500.00" />
          </div>
          <div className={styles.field}>
            <label htmlFor="customs_hs_code">HS Code</label>
            <input id="customs_hs_code" name="customs_hs_code" value={form.customs_hs_code} onChange={handleChange} placeholder="e.g. 970110 (Paintings)" />
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="customs_origin_country">Country of Origin</label>
            <input id="customs_origin_country" name="customs_origin_country" value={form.customs_origin_country} onChange={handleChange} placeholder="US" />
          </div>
          <div className={styles.field}>
            <label htmlFor="customs_contents">Contents Type</label>
            <select id="customs_contents" name="customs_contents" value={form.customs_contents} onChange={handleChange}>
              <option value="merchandise">Merchandise</option>
              <option value="gift">Gift</option>
              <option value="documents">Documents</option>
              <option value="returned_goods">Returned goods</option>
              <option value="sample">Sample</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="customs_non_delivery">If undeliverable</label>
            <select id="customs_non_delivery" name="customs_non_delivery" value={form.customs_non_delivery} onChange={handleChange}>
              <option value="return">Return to sender</option>
              <option value="treat_as_abandoned">Treat as abandoned</option>
            </select>
          </div>
          <div className={styles.field}></div>
        </div>
      </fieldset>
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <button className={styles.submitButton} type="submit" disabled={submitting}>Get Shipping Rates</button>
      </div>
    </form>
  );
};

export default CreateShipmentForm; 